import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../hooks/useAuth';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { totalItems } = useCart();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    const handler = () => setUserMenuOpen(false);
    if (userMenuOpen) document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [userMenuOpen]);

  const navLinks = [
    { label: 'Ana Sayfa', path: '/' },
    { label: 'Günübirlik Turlar', path: '/gunubirlik-turlar' },
    { label: 'Konaklamalı Turlar', path: '/konaklamali-turlar' },
    { label: 'Ulaşımlı Tatil', path: '/ulasimli-tatil' },
    { label: 'Yurt Dışı Turlar', path: '/yurtdisi-turlar' },
  ];

  const isActive = (path: string) => location.pathname === path;
  const isTransparent = !scrolled && !menuOpen && location.pathname === '/';
  const isDark = theme === 'dark';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen || location.pathname !== '/'
          ? isDark
            ? 'bg-gray-900 border-b border-gray-700'
            : 'bg-white shadow-sm border-b border-gray-100'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src="https://storage.readdy-site.link/project_files/904466f0-2c05-4555-9f8d-869e0816a927/e2d94e9e-889c-4573-8429-0052ad575f89_Adsz-tasarm-3.png?v=589ebfa6144df8359c3a9f9f79357ea7"
            alt="Ankara Travelers Logo"
            className="h-12 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map(link => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                isActive(link.path)
                  ? 'text-orange-500 bg-orange-50 dark:bg-orange-950/40'
                  : isTransparent
                  ? 'text-white hover:text-orange-200'
                  : isDark
                  ? 'text-gray-300 hover:text-orange-400 hover:bg-gray-800'
                  : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          {/* Dark/Light Mode Toggle */}
          <button
            onClick={toggleTheme}
            title={isDark ? 'Aydınlık Moda Geç' : 'Karanlık Moda Geç'}
            className={`hidden lg:flex w-9 h-9 items-center justify-center rounded-full transition-all duration-200 cursor-pointer border ${
              isTransparent
                ? 'border-white/30 text-white hover:bg-white/10'
                : isDark
                ? 'border-gray-600 text-gray-300 hover:border-orange-400 hover:text-orange-400 bg-gray-800'
                : 'border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500 bg-white'
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={isDark ? 'ri-sun-line text-base' : 'ri-moon-line text-base'} />
            </div>
          </button>

          <Link
            to="/ara"
            className={`hidden lg:flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
              isTransparent
                ? 'border-white/30 text-white hover:bg-white/10'
                : isDark
                ? 'border-gray-600 text-gray-300 hover:border-orange-400 hover:text-orange-400 bg-gray-800'
                : 'border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500 bg-white'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line" />
            </div>
            <span>Tur Ara</span>
          </Link>

          {/* User auth button */}
          {user ? (
            <div className="relative hidden lg:block" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                  isTransparent
                    ? 'border-white/30 text-white hover:bg-white/10'
                    : isDark
                    ? 'border-gray-600 text-gray-300 hover:border-orange-400 bg-gray-800'
                    : 'border-gray-200 text-gray-700 hover:border-orange-400 bg-white'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center bg-orange-500 rounded-full">
                  <span className="text-white text-xs font-bold">{user.firstName[0]}</span>
                </div>
                <span>{user.firstName}</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl border border-gray-100 py-1 z-50">
                  <div className="px-4 py-2.5 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900">{user.firstName} {user.lastName}</p>
                    <p className="text-xs text-gray-400 truncate">{user.email}</p>
                  </div>
                  <button
                    onClick={() => { logout(); setUserMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-logout-box-r-line" />
                    Çıkış Yap
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/giris"
              className={`hidden lg:flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                isTransparent
                  ? 'border-white/30 text-white hover:bg-white/10'
                  : isDark
                  ? 'border-gray-600 text-gray-300 hover:border-orange-400 hover:text-orange-400 bg-gray-800'
                  : 'border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500 bg-white'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-line" />
              </div>
              <span>Giriş Yap</span>
            </Link>
          )}

          <Link
            to="/sepet"
            className="relative flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-shopping-cart-2-line text-base" />
            </div>
            <span>Sepetim</span>
            {totalItems > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-bold">
                {totalItems}
              </span>
            )}
          </Link>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-lg cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl ${isTransparent ? 'text-white' : isDark ? 'text-gray-300' : 'text-gray-700'}`} />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className={`lg:hidden border-t px-6 py-4 flex flex-col gap-1 ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-100'}`}>
          <button
            onClick={toggleTheme}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
              isDark ? 'text-gray-300 hover:text-orange-400 hover:bg-gray-800' : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={isDark ? 'ri-sun-line' : 'ri-moon-line'} />
            </div>
            {isDark ? 'Aydınlık Mod' : 'Karanlık Mod'}
          </button>
          <Link
            to="/ara"
            onClick={() => setMenuOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-orange-500 bg-orange-50 cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-search-line" /></div>
            Tur Ara
          </Link>
          {navLinks.map(link => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMenuOpen(false)}
              className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                isActive(link.path)
                  ? 'text-orange-500 bg-orange-50'
                  : isDark
                  ? 'text-gray-300 hover:text-orange-400 hover:bg-gray-800'
                  : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
          {user ? (
            <>
              <div className={`px-4 py-3 rounded-lg text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <p className="font-semibold">{user.firstName} {user.lastName}</p>
                <p className="text-xs text-gray-400">{user.email}</p>
              </div>
              <button
                onClick={() => { logout(); setMenuOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 cursor-pointer"
              >
                <i className="ri-logout-box-r-line" />
                Çıkış Yap
              </button>
            </>
          ) : (
            <Link
              to="/giris"
              onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                isDark ? 'text-gray-300 hover:text-orange-400 hover:bg-gray-800' : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50'
              }`}
            >
              <i className="ri-user-line" />
              Giriş Yap / Kayıt Ol
            </Link>
          )}
        </div>
      )}
    </nav>
  );
}
